import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * Two-column compare — before/after, with-vs-without, problem/solution.
 *
 * Routes from a 2-column GFM table with up to 3 rows of short cells, OR
 * explicit `:::compare` markdown directives in the source.
 */
export type CompareSide = {
  label: ReactNode;
  body: ReactNode;
  /** Tone for this side. */
  tone?: "rose" | "emerald" | "blue" | "violet" | "amber" | "stone";
  /** Optional emoji marker. */
  marker?: string;
};

export type CompareCardProps = {
  left: CompareSide;
  right: CompareSide;
  /** Optional small heading above the two sides. */
  title?: ReactNode;
  className?: string;
};

const TONES: Record<NonNullable<CompareSide["tone"]>, string> = {
  rose: "bg-rose-100",
  emerald: "bg-emerald-100",
  blue: "bg-sky-100",
  violet: "bg-violet-100",
  amber: "bg-amber-100",
  stone: "bg-stone-200",
};

export function CompareCard({
  left,
  right,
  title,
  className,
}: CompareCardProps) {
  return (
    <div className={cn("my-6", className)}>
      {title ? (
        <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-stone-500 mb-3">
          {title}
        </p>
      ) : null}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <CompareSidePane side={left} />
        <CompareSidePane side={right} />
      </div>
    </div>
  );
}

function CompareSidePane({ side }: { side: CompareSide }) {
  const tone = side.tone ?? "stone";
  return (
    <div
      className={cn(
        "rounded-2xl px-5 py-4 sm:px-6 sm:py-5 min-h-[120px]",
        TONES[tone],
      )}
    >
      <div className="flex items-center gap-2 mb-2">
        {side.marker ? (
          <span className="text-base" aria-hidden>
            {side.marker}
          </span>
        ) : null}
        <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-stone-700/65">
          {side.label}
        </p>
      </div>
      <div className="text-[14px] leading-relaxed text-stone-800">
        {side.body}
      </div>
    </div>
  );
}
